import { useEffect, useState } from "react";
import Card from "@mui/material/Card";
import {
  Box,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  MenuItem,
  Select,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import CallMadeIcon from "@mui/icons-material/CallMade";
import ToggleIcon from "@mui/icons-material/ToggleOn";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import { Check } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import PropTypes from "prop-types";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";

import {
  fetchStatusList,
  getStatusMappings,
  editStatusMapping,
  saveStatusMapping,
} from "api/apiService";
import { notifyCenter } from "utils/toast";

const SELECT_SX = {
  height: 40,
  "&.MuiInputBase-root": {
    display: "flex !important",
    placeItems: "initial !important",
    padding: "0 !important",
  },
  "& .MuiSelect-select": {
    display: "flex !important",
    alignItems: "center !important",
    minHeight: "40px !important",
    boxSizing: "border-box",
    padding: "0 32px 0 14px !important",
    flex: 1,
    width: "100% !important",
  },
};

const DIRECTION_OPTIONS = [
  { value: "both",        label: "Begge retninger",  description: "Brukes b\u00e5de ved WC-webhook og bmax-push", icon: SwapHorizIcon },
  { value: "wc_to_bmax",  label: "Kun WC \u2192 Bmax", description: "Kun innkommende fra WooCommerce",             icon: CallReceivedIcon },
  { value: "bmax_to_wc",  label: "Kun Bmax \u2192 WC", description: "Kun utg\u00e5ende til WooCommerce",            icon: CallMadeIcon },
];

// Fallback WC statuses if backend can't reach WP. Array of { slug, label }.
const FALLBACK_WC_STATUSES = [
  { slug: "pending",        label: "Pending payment" },
  { slug: "processing",     label: "Processing" },
  { slug: "on-hold",        label: "On hold" },
  { slug: "completed",      label: "Completed" },
  { slug: "cancelled",      label: "Cancelled" },
  { slug: "refunded",       label: "Refunded" },
  { slug: "failed",         label: "Failed" },
  { slug: "checkout-draft", label: "Draft" },
];

function ToggleCard({ active, icon: Icon, label, description, onToggle }) {
  return (
    <Box
      onClick={onToggle}
      sx={(t) => ({
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        border: `1.5px solid ${active ? t.palette.info.main : t.palette.grey[300]}`,
        borderRadius: 2,
        px: 2,
        py: 1.5,
        cursor: "pointer",
        backgroundColor: active ? t.palette.info.light + "20" : "#fff",
        transition: "all 150ms ease",
        "&:hover": {
          backgroundColor: active ? t.palette.info.light + "33" : t.palette.grey[50],
          borderColor: active ? t.palette.info.main : t.palette.grey[400],
        },
      })}
    >
      <SoftBox display="flex" alignItems="center" gap={1.5}>
        <SoftBox
          sx={(t) => ({
            background: active
              ? `linear-gradient(195deg, ${t.palette.info.main}, ${t.palette.info.dark || t.palette.info.main})`
              : t.palette.grey[400],
            color: "#fff",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 32,
            height: 32,
          })}
        >
          <Icon sx={{ fontSize: 18 }} />
        </SoftBox>
        <SoftBox>
          <SoftTypography variant="button" fontWeight="bold" sx={{ textTransform: "none" }} display="block">
            {label}
          </SoftTypography>
          {description && (
            <SoftTypography variant="caption" color="text" sx={{ fontSize: 11 }}>
              {description}
            </SoftTypography>
          )}
        </SoftBox>
      </SoftBox>

      <SoftBox
        sx={(t) => ({
          width: 22,
          height: 22,
          borderRadius: "6px",
          border: `2px solid ${active ? t.palette.info.main : t.palette.grey[400]}`,
          backgroundColor: active ? t.palette.info.main : "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
        })}
      >
        {active && <Check sx={{ fontSize: 14 }} />}
      </SoftBox>
    </Box>
  );
}

ToggleCard.propTypes = {
  active: PropTypes.bool.isRequired,
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  description: PropTypes.string,
  onToggle: PropTypes.func.isRequired,
};

function CreateStatusMapping() {
  const navigate = useNavigate();
  const { mappingId } = useParams();
  const isEditMode = !!mappingId;

  const [isLoading, setIsLoading] = useState(true);
  const [wcStatusOptions, setWcStatusOptions] = useState(FALLBACK_WC_STATUSES);
  const [bmaxStatusList, setBmaxStatusList] = useState([]);

  const [formData, setFormData] = useState({
    wc_status: "",
    bmax_status: "",
    direction: "both",
    auto_transition: true,
    guard_pre_payment: false,
  });

  useEffect(() => {
    let cancelled = false;

    async function init() {
      setIsLoading(true);
      try {
        const [statusesRes, mappingsMeta] = await Promise.all([
          fetchStatusList(),
          getStatusMappings({ limit: 1, page_number: 1 }).catch(() => null),
        ]);

        if (cancelled) return;

        if (statusesRes?.status && Array.isArray(statusesRes.status_list)) {
          setBmaxStatusList(statusesRes.status_list);
        }
        // Prefer richer wc_statuses ({slug,label,wc_slug}); fall back to
        // wc_status_options (pure slugs) if plugin endpoint missing.
        if (mappingsMeta?.ok && Array.isArray(mappingsMeta.wc_statuses) && mappingsMeta.wc_statuses.length) {
          setWcStatusOptions(mappingsMeta.wc_statuses);
        } else if (mappingsMeta?.ok && Array.isArray(mappingsMeta.wc_status_options)) {
          setWcStatusOptions(
            mappingsMeta.wc_status_options.map((slug) => ({ slug, label: slug }))
          );
        }

        if (isEditMode) {
          const res = await editStatusMapping(mappingId);
          if (!cancelled && res?.ok && res.mapping) {
            const m = res.mapping;
            setFormData({
              wc_status: m.wc_status,
              bmax_status: m.bmax_status,
              direction: m.direction,
              auto_transition: !!m.auto_transition,
              guard_pre_payment: !!m.guard_pre_payment,
            });
          } else if (!cancelled) {
            notifyCenter("error", res?.message || "Fant ikke mappingen", { duration: 3000 });
          }
        }
      } catch (e) {
        console.error("init error", e);
        if (!cancelled) notifyCenter("error", "Noe gikk galt ved henting", { duration: 3000 });
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    init();
    return () => { cancelled = true; };
  }, [mappingId, isEditMode]);

  function setField(field, value) {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e) {
    e?.preventDefault?.();

    const missing = [];
    if (!formData.wc_status) missing.push("WC-status");
    if (!formData.bmax_status) missing.push("Bmax-status");
    if (missing.length) {
      notifyCenter("warning", "Vennligst fyll ut: " + missing.join(", "), { duration: 3000 });
      return;
    }

    const payload = {
      ...formData,
      ...(isEditMode ? { mapping_id: mappingId } : {}),
    };

    setIsLoading(true);
    try {
      const res = await saveStatusMapping(payload);
      if (res?.ok) {
        notifyCenter("success", isEditMode ? "Mapping oppdatert" : "Mapping opprettet", { duration: 2000 });
        navigate("/status-mappings");
      } else {
        notifyCenter("error", res?.message || res?.error || "Kunne ikke lagre", { duration: 3000 });
      }
    } catch (err) {
      console.error("save mapping error", err);
      notifyCenter("error", "Noe gikk galt ved lagring", { duration: 3000 });
    } finally {
      setIsLoading(false);
    }
  }

  const isBmaxToWcOnly = formData.direction === "bmax_to_wc";

  return (
    <DashboardLayout>
      <SoftBox py={3} px={{ xs: 1, md: 2 }}>
        <Card sx={{ position: "relative" }}>
          {/* Header */}
          <SoftBox p={3}>
            <SoftTypography variant="h5" fontWeight="bold">
              {isEditMode ? "Rediger mapping" : "Ny status-mapping"}
            </SoftTypography>
            <SoftTypography variant="button" color="text">
              Konfigurer hvordan en WC-status korresponderer til en bmax-status
            </SoftTypography>
          </SoftBox>
          <Divider sx={{ my: 0 }} />

          {/* Info callout */}
          <SoftBox px={3} pt={3}>
            <SoftBox
              sx={(t) => ({
                display: "flex",
                gap: 1.5,
                p: 2,
                borderRadius: 1.5,
                backgroundColor: t.palette.info.light + "15",
                borderLeft: `4px solid ${t.palette.info.main}`,
              })}
            >
              <InfoOutlinedIcon sx={{ color: "info.main", mt: 0.25 }} />
              <SoftTypography variant="caption" color="text" component="div" sx={{ lineHeight: 1.6 }}>
                <strong>Auto-transisjon</strong> (for WC→Bmax-retning): hvis <em>p\u00e5</em>, oppdateres
                bmax-status automatisk n\u00e5r webhook med denne WC-statusen mottas. Hvis <em>av</em>,
                logges kun hendelsen.
                <br />
                <strong>Pre-payment guard</strong>: auto-transisjon kjører kun hvis bmax-ordren er
                i pre-payment tilstand (&laquo;Kvalitetssikres&raquo;, &laquo;Venter p\u00e5 betaling&raquo;). Typisk
                brukt for <code>processing</code> for \u00e5 ikke overstyre admin-valg.
              </SoftTypography>
            </SoftBox>
          </SoftBox>

          {/* Form */}
          <SoftBox component="form" onSubmit={handleSubmit} p={3}>
            <Grid container spacing={2.5}>
              {/* WC + Bmax statuses */}
              <Grid item xs={12} sm={6}>
                <SoftTypography variant="caption" fontWeight="bold" color="text" mb={0.5} display="block">
                  WC-status
                </SoftTypography>
                <FormControl fullWidth size="small">
                  <Select
                    value={formData.wc_status}
                    displayEmpty
                    onChange={(e) => setField("wc_status", e.target.value)}
                    sx={SELECT_SX}
                    renderValue={(value) => {
                      if (!value) {
                        return (
                          <SoftTypography variant="body2" color="secondary">
                            Velg WC-status
                          </SoftTypography>
                        );
                      }
                      const found = wcStatusOptions.find((s) => s.slug === value);
                      return (
                        <SoftTypography variant="body2" color="text">
                          {found?.label || value}{" "}
                          <span style={{ fontFamily: "monospace", fontSize: 11, color: "#888" }}>
                            ({value})
                          </span>
                        </SoftTypography>
                      );
                    }}
                  >
                    <MenuItem value="" disabled>Velg WC-status</MenuItem>
                    {wcStatusOptions.map((s) => (
                      <MenuItem key={s.slug} value={s.slug}>
                        <span>
                          {s.label}{" "}
                          <code style={{ fontSize: 11, color: "#888" }}>({s.slug})</code>
                        </span>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <SoftTypography variant="caption" fontWeight="bold" color="text" mb={0.5} display="block">
                  Bmax-status
                </SoftTypography>
                <FormControl fullWidth size="small">
                  <Select
                    value={formData.bmax_status}
                    displayEmpty
                    onChange={(e) => setField("bmax_status", e.target.value)}
                    sx={SELECT_SX}
                    renderValue={(value) => {
                      if (!value) {
                        return (
                          <SoftTypography variant="body2" color="secondary">
                            Velg bmax-status
                          </SoftTypography>
                        );
                      }
                      const item = bmaxStatusList.find((s) => s.value === value);
                      return (
                        <SoftTypography variant="body2" color="text">
                          {item?.label || value}
                        </SoftTypography>
                      );
                    }}
                  >
                    <MenuItem value="" disabled>Velg bmax-status</MenuItem>
                    {bmaxStatusList.map((s) => (
                      <MenuItem key={s.value} value={s.value}>
                        {s.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Direction */}
              <Grid item xs={12}>
                <SoftTypography variant="caption" fontWeight="bold" color="text" mb={1} display="block">
                  Retning
                </SoftTypography>
                <Grid container spacing={2}>
                  {DIRECTION_OPTIONS.map((opt) => {
                    const Icon = opt.icon;
                    const active = formData.direction === opt.value;
                    return (
                      <Grid item xs={12} sm={4} key={opt.value}>
                        <Box
                          onClick={() => setField("direction", opt.value)}
                          sx={(t) => ({
                            border: `1.5px solid ${active ? t.palette.info.main : t.palette.grey[300]}`,
                            borderRadius: 2,
                            p: 2,
                            cursor: "pointer",
                            backgroundColor: active ? t.palette.info.light + "20" : "#fff",
                            transition: "all 150ms ease",
                            "&:hover": {
                              backgroundColor: active ? t.palette.info.light + "33" : t.palette.grey[50],
                              borderColor: active ? t.palette.info.main : t.palette.grey[400],
                            },
                          })}
                        >
                          <SoftBox display="flex" alignItems="center" gap={1} mb={0.5}>
                            <SoftBox
                              sx={(t) => ({
                                background: active
                                  ? `linear-gradient(195deg, ${t.palette.info.main}, ${t.palette.info.dark || t.palette.info.main})`
                                  : t.palette.grey[400],
                                color: "#fff",
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 28,
                                height: 28,
                              })}
                            >
                              <Icon sx={{ fontSize: 16 }} />
                            </SoftBox>
                            <SoftTypography variant="button" fontWeight="bold" sx={{ textTransform: "none" }}>
                              {opt.label}
                            </SoftTypography>
                          </SoftBox>
                          <SoftTypography variant="caption" color="text">
                            {opt.description}
                          </SoftTypography>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Grid>

              {/* Auto-transition + Guard */}
              <Grid item xs={12} sm={6}>
                <ToggleCard
                  active={formData.auto_transition}
                  icon={ToggleIcon}
                  label="Auto-transisjon"
                  description={
                    isBmaxToWcOnly
                      ? "Ikke relevant for utg\u00e5ende"
                      : "Oppdater bmax-status ved WC-webhook"
                  }
                  onToggle={() => setField("auto_transition", !formData.auto_transition)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ToggleCard
                  active={formData.guard_pre_payment}
                  icon={ShieldOutlinedIcon}
                  label="Pre-payment guard"
                  description="Kun transisjon fra pre-payment status"
                  onToggle={() => setField("guard_pre_payment", !formData.guard_pre_payment)}
                />
              </Grid>
            </Grid>
          </SoftBox>

          {/* Footer */}
          <Divider sx={{ my: 0 }} />
          <SoftBox display="flex" justifyContent="flex-end" gap={1} p={3}>
            <SoftButton
              variant="outlined"
              color="secondary"
              size="small"
              onClick={() => navigate("/status-mappings")}
            >
              Avbryt
            </SoftButton>
            <SoftButton
              variant="gradient"
              color="info"
              size="small"
              type="submit"
              onClick={handleSubmit}
            >
              {isEditMode ? "Oppdater" : "Lagre"}
            </SoftButton>
          </SoftBox>

          {isLoading && (
            <SoftBox
              sx={{
                position: "absolute",
                inset: 0,
                backgroundColor: "rgba(255, 255, 255, 0.85)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 10,
                borderRadius: "inherit",
              }}
            >
              <CircularProgress color="info" size={48} />
            </SoftBox>
          )}
        </Card>
      </SoftBox>
    </DashboardLayout>
  );
}

export default CreateStatusMapping;
